import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';
import { downloadAuthedFile } from './authed-file';

/**
 * Engagement-archive surface (T-0060 / E05-F03). The full closeout flow
 * lives in three calls:
 *
 *   1. POST  /api/v1/engagements/:id/archive — kicks off an async build.
 *   2. GET   /api/v1/engagements/:id/archive/:archive_id — polls status;
 *      a `download_url` appears once `build_status === 'succeeded'`. It is
 *      an API path behind sign-in (the blob is private), so the ZIP is
 *      fetched with the bearer token (`useDownloadArchiveMutation`).
 *   3. POST  /api/v1/engagements/:id/archive/:archive_id/handoff —
 *      records the one-shot handoff transition for a succeeded archive.
 */

export const ArchiveBuildStatusSchema = z.union([
  z.literal('building'),
  z.literal('succeeded'),
  z.literal('failed'),
]);
export type ArchiveBuildStatus = z.infer<typeof ArchiveBuildStatusSchema>;

export const EngagementArchiveSchema = z.object({
  archive_id: z.string().uuid(),
  engagement_id: z.string().uuid(),
  build_status: ArchiveBuildStatusSchema,
  manifest: z.unknown().nullable(),
  archive_content_hash: z.string().nullable(),
  folder_schema_version: z.string(),
  build_error: z.string().nullable(),
  built_by: z.string(),
  built_at: z.string().nullable(),
  handed_off_at: z.string().nullable(),
  created_at: z.string(),
  download_url: z.string().nullable(),
});
export type EngagementArchive = z.infer<typeof EngagementArchiveSchema>;

export const CreateEngagementArchiveResultSchema = z.object({
  archive_id: z.string().uuid(),
  build_status: z.literal('building'),
});
export type CreateEngagementArchiveResult = z.infer<typeof CreateEngagementArchiveResultSchema>;

export const HandoffResultSchema = z.object({
  archive_id: z.string().uuid(),
  handed_off_at: z.string(),
  download_url: z.string().nullable(),
});
export type HandoffResult = z.infer<typeof HandoffResultSchema>;

export const ListArchivesResultSchema = z.object({
  items: z.array(EngagementArchiveSchema),
  next_cursor: z.string().nullable(),
});
export type ListArchivesResult = z.infer<typeof ListArchivesResultSchema>;

export function listArchives(
  engagementId: string,
  opts?: { build_status?: ArchiveBuildStatus; cursor?: string; limit?: number },
): Promise<ListArchivesResult> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/archive`,
    ListArchivesResultSchema,
    { query: opts as Record<string, string | number | boolean | undefined> },
  );
}

export function getArchive(engagementId: string, archiveId: string): Promise<EngagementArchive> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/archive/${encodeURIComponent(archiveId)}`,
    EngagementArchiveSchema,
  );
}

export function createArchive(
  engagementId: string,
  idempotencyKey?: string,
): Promise<CreateEngagementArchiveResult> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/archive`,
    CreateEngagementArchiveResultSchema,
    {
      method: 'POST',
      ...(idempotencyKey ? { idempotencyKey } : {}),
    },
  );
}

export interface HandoffInput {
  recipient: string;
  delivery_method?: string;
}

export function handoffArchive(
  engagementId: string,
  archiveId: string,
  input: HandoffInput,
  idempotencyKey?: string,
): Promise<HandoffResult> {
  return apiClient(
    `/api/v1/engagements/${encodeURIComponent(engagementId)}/archive/${encodeURIComponent(
      archiveId,
    )}/handoff`,
    HandoffResultSchema,
    {
      method: 'POST',
      body: input,
      ...(idempotencyKey ? { idempotencyKey } : {}),
    },
  );
}

export function useArchiveListQuery(engagementId: string | undefined) {
  return useQuery({
    queryKey: ['archive', 'list', engagementId] as const,
    queryFn: () => listArchives(engagementId!),
    enabled: Boolean(engagementId),
  });
}

export function useArchiveQuery(
  engagementId: string | undefined,
  archiveId: string | null | undefined,
  opts: { pollMs?: number } = {},
) {
  return useQuery({
    queryKey: ['archive', 'detail', engagementId, archiveId] as const,
    queryFn: () => getArchive(engagementId!, archiveId!),
    enabled: Boolean(engagementId && archiveId),
    refetchInterval: opts.pollMs ?? false,
  });
}

export function useCreateArchiveMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => createArchive(engagementId!, idempotencyKeyForCreate(engagementId ?? '')),
    onSuccess: () => {
      if (engagementId) {
        void qc.invalidateQueries({ queryKey: ['archive', 'list', engagementId] });
      }
    },
  });
}

export function useHandoffArchiveMutation(
  engagementId: string | undefined,
  archiveId: string | null | undefined,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: HandoffInput) =>
      handoffArchive(
        engagementId!,
        archiveId!,
        input,
        idempotencyKeyForHandoff(engagementId ?? '', archiveId ?? ''),
      ),
    onSuccess: () => {
      if (engagementId && archiveId) {
        void qc.invalidateQueries({ queryKey: ['archive', 'detail', engagementId, archiveId] });
        void qc.invalidateQueries({ queryKey: ['archive', 'list', engagementId] });
      }
    },
  });
}

/**
 * Save a built archive. Its download_url needs the bearer token, which a
 * plain link never sends, so the ZIP is fetched and saved as a file.
 */
export function useDownloadArchiveMutation() {
  return useMutation({
    mutationFn: (archive: { archive_id: string; download_url: string }) =>
      downloadAuthedFile(archive.download_url, `${archive.archive_id}.zip`),
  });
}

/**
 * Per-session, stable Idempotency-Key helpers. The frontend doesn't need
 * cross-session dedup — accidental double-clicks within the same browser
 * tab are the threat model. A timestamped key keeps each user-initiated
 * action distinct while collapsing the same click's double-fire.
 */
function idempotencyKeyForCreate(engagementId: string): string {
  return `web-archive-create-${engagementId}-${Math.floor(Date.now() / 60_000)}`;
}

function idempotencyKeyForHandoff(engagementId: string, archiveId: string): string {
  return `web-archive-handoff-${engagementId}-${archiveId}-${Math.floor(Date.now() / 60_000)}`;
}
